import React, { useState } from 'react'
import { ClickAwayListener, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import LaunchIcon from '@material-ui/icons/Launch'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import classNames from 'classnames'
import { Typography, Paper } from '@paintscout/ui'
import type { StyleClasses } from '@paintscout/ui/styles'
import DropdownSelectSearchBar from '@paintscout/ui/components/DropdownSelect/DropdownSelectSearchBar'
import DropdownSelectMenuItem from '../DropdownSelect/DropdownSelectMenuItem'
import type { Details } from '../DetailsContainer/DetailsContainer'

const useStyles = makeStyles<Theme, DetailDocListProps>(
  (theme) => ({
    root: {},
    noDetailsItem: {
      display: 'flex',
      justifyContent: 'center',
      height: 200,
      alignItems: 'center',
      whiteSpace: 'pre-wrap',
      color: theme.palette.text.disabled
    },
    detailsCopyIcon: {
      fontSize: '1.5rem',
      marginLeft: theme.spacing(),
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    detailsDocItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`
      }
    },
    linkHover: {
      '&:hover': {
        cursor: 'pointer',
        color: theme.paintscout.palette.brand.Neutral.P40
      }
    },
    selectIcon: {
      fontSize: '1.5rem',
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    dropdownContainer: {
      position: 'relative' as const
    },
    dropdownPaper: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      zIndex: theme.zIndex.modal,
      maxHeight: 300,
      overflow: 'auto',
      boxShadow: theme.shadows[4]
    },
    menuItemsContainer: {
      maxHeight: 250,
      overflow: 'auto',
      paddingBottom: theme.spacing(1)
    },
    unselectItem: {
      margin: theme.spacing(0.25, 2),
      padding: theme.spacing(1, 1.5),
      borderRadius: theme.shape.borderRadius,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  }),
  { name: 'DetailDocList' }
)

export interface DetailDocListProps {
  classes?: StyleClasses<typeof useStyles>
  details: Details[]
}

function DetailDocList(props: DetailDocListProps) {
  const classes = useStyles(props)
  const { details } = props
  const { enqueueSnackbar } = useSnackbar()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [searchFilter, setSearchFilter] = useState('')

  const handleCopy = (value: string, title: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        enqueueSnackbar(`${title} Copied`, { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(`Failed to copy ${title}`, { variant: 'error' })
      })
  }

  const handleClose = () => {
    setOpenIndex(null)
    setSearchFilter('')
  }

  const handleToggleDropdown = (event: React.MouseEvent<HTMLElement>, index: number) => {
    event.stopPropagation()
    if (openIndex === index) {
      handleClose()
    } else {
      setOpenIndex(index)
      setSearchFilter('')
    }
  }

  const handleSelect = (detail: Details, option: { label: string; value: string } | null) => {
    detail.onSelect?.(option)
    handleClose()
  }

  return (
    <div>
      {details.length === 0 && (
        <div className={classes.noDetailsItem}>
          <Typography variant="subtitle1">No details found.</Typography>
        </div>
      )}
      {details.map((detail, index) => {
        const hasSelect = detail.selectOptions && detail.onSelect
        const isOpen = openIndex === index

        const rowInner = (
          <>
            <div
              onClick={(e) => {
                if (hasSelect) {
                  handleToggleDropdown(e, index)
                } else if (detail.canCopy && !detail.link) {
                  handleCopy(detail.value as string, detail.title)
                } else if (detail.link && !detail.canCopy) {
                  detail.link()
                }
              }}
              className={classes.detailsDocItem}
            >
              <div>
                <Typography style={{ display: 'flex', alignItems: 'center' }} variant={'body2'} color="textSecondary">
                  {detail.title}
                  {detail?.icon ? detail.icon : null}
                </Typography>
                <Typography
                  variant={'body1'}
                  onClick={detail.link ? detail.link : null}
                  className={classNames({
                    [classes.linkHover]: detail?.link
                  })}
                >
                  {detail.value}
                </Typography>
              </div>
              <div>
                {hasSelect && (
                  <KeyboardArrowDownIcon
                    className={classes.selectIcon}
                    style={{ transform: isOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}
                  />
                )}
                {detail.canCopy && (
                  <FileCopyIcon
                    className={classes.detailsCopyIcon}
                    onClick={() =>
                      detail.canCopy && detail.link ? handleCopy(detail.value as string, detail.title) : null
                    }
                  />
                )}
                {detail.link && !detail.canCopy && (
                  <LaunchIcon className={classes.detailsCopyIcon} onClick={detail.link} />
                )}
              </div>
            </div>
            {hasSelect && isOpen && (
              <div className={classes.dropdownContainer}>
                <Paper className={classes.dropdownPaper}>
                  <DropdownSelectSearchBar
                    placeholder={`Search ${detail.title.toLowerCase()}s`}
                    onChange={(val) => setSearchFilter(val)}
                  />
                  <div className={classes.menuItemsContainer}>
                    {detail.selectOptions
                      .filter((opt) => opt.label.toLowerCase().includes(searchFilter.toLowerCase()))
                      .map((opt) => (
                        <DropdownSelectMenuItem
                          key={opt.value}
                          option={opt}
                          selected={detail.selectedValue === opt.value}
                          searchable
                          onClick={() => handleSelect(detail, opt)}
                        />
                      ))}
                    {detail.selectedValue && (
                      <div className={classes.unselectItem} onClick={() => handleSelect(detail, null)}>
                        <Typography color="textSecondary" variant="body2">
                          Clear selection
                        </Typography>
                      </div>
                    )}
                  </div>
                </Paper>
              </div>
            )}
          </>
        )

        if (hasSelect) {
          return (
            <ClickAwayListener key={index} onClickAway={isOpen ? handleClose : () => {}}>
              <div>{rowInner}</div>
            </ClickAwayListener>
          )
        }

        return <div key={index}>{rowInner}</div>
      })}
    </div>
  )
}

export default DetailDocList
